import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGift } from '../../../context/GiftContext';
import { useAuth } from '../../../context/AuthContext';
import { useTeam } from '../../../context/TeamContext';
import { GIFT_CONTEXTS } from '../../../utils/nurseGiftManager';
import GiftDisplay from './GiftDisplay';
import CoinsExchange from '../../TCoins/CoinsExchange';
import NurseBottomNavigation from '../Layout/NurseBottomNavigation';

const GiftHistoryPage = () => {
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();
  const { receivedGifts, sentGifts, getReceivedGiftStats, getSentGiftStats } = useGift();
  const [searchParams] = useSearchParams();
  
  // Check if there's a tab parameter in URL (e.g., from notification)
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'received'); // 'received', 'sent', 'exchange'
  const [filterContext, setFilterContext] = useState('all'); // 'all', 'PERSONAL', 'SHIFT_SWAP', etc.
  
  // Update activeTab when URL changes
  useEffect(() => {
    if (tabFromUrl && ['received', 'sent', 'exchange'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  // Filter gifts by context
  const filterGifts = (gifts) => {
    if (filterContext === 'all') return gifts;
    return gifts.filter(gift => gift.context === filterContext);
  };

  const filteredReceivedGifts = filterGifts(receivedGifts);
  const filteredSentGifts = filterGifts(sentGifts);

  const receivedStats = getReceivedGiftStats();
  const sentStats = getSentGiftStats();

  const getContextLabel = (context) => {
    switch (context) {
      case GIFT_CONTEXTS.PERSONAL:
        return 'ให้กำลังใจ';
      case GIFT_CONTEXTS.SHIFT_SWAP:
        return 'สินน้ำใจแลกเวร';
      case GIFT_CONTEXTS.GAMIFICATION_REWARD:
        return 'รางวัล Gamify';
      case GIFT_CONTEXTS.TEAM_APPRECIATION:
        return 'แสดงความขอบคุณ';
      default:
        return context;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">🎁 ประวัติของขวัญ</h1>
          <p className="text-gray-600">ดูประวัติการให้และรับของขวัญ</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('received')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'received'
                ? 'text-pink-600 border-b-2 border-pink-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            ของขวัญที่ได้รับ ({receivedGifts.length})
          </button>
          <button
            onClick={() => setActiveTab('sent')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'sent'
                ? 'text-pink-600 border-b-2 border-pink-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            ของขวัญที่ให้ ({sentGifts.length})
          </button>
          <button
            onClick={() => setActiveTab('exchange')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'exchange'
                ? 'text-pink-600 border-b-2 border-pink-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            แลกเป็นเงินสด
          </button>
        </div>

        {/* Context Filter */}
        {activeTab !== 'exchange' && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              กรองตามประเภท:
            </label>
            <select
              value={filterContext}
              onChange={(e) => setFilterContext(e.target.value)}
              className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500"
            >
              <option value="all">ทั้งหมด</option>
              <option value={GIFT_CONTEXTS.PERSONAL}>{getContextLabel(GIFT_CONTEXTS.PERSONAL)}</option>
              <option value={GIFT_CONTEXTS.SHIFT_SWAP}>{getContextLabel(GIFT_CONTEXTS.SHIFT_SWAP)}</option>
              <option value={GIFT_CONTEXTS.GAMIFICATION_REWARD}>{getContextLabel(GIFT_CONTEXTS.GAMIFICATION_REWARD)}</option>
              <option value={GIFT_CONTEXTS.TEAM_APPRECIATION}>{getContextLabel(GIFT_CONTEXTS.TEAM_APPRECIATION)}</option>
            </select>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'received' && (
          <div className="space-y-6">
            {/* Stats */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">📊 สถิติของขวัญที่ได้รับ</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center bg-gray-50 p-4 rounded-lg">
                  <div className="text-2xl mb-2">🌹</div>
                  <div className="text-xl font-bold text-red-600">{receivedStats.rose || 0}</div>
                  <div className="text-sm text-gray-600">ดอกกุหลาบ</div>
                </div>
                <div className="text-center bg-gray-50 p-4 rounded-lg">
                  <div className="text-2xl mb-2">💐</div>
                  <div className="text-xl font-bold text-pink-600">{receivedStats.bouquet || 0}</div>
                  <div className="text-sm text-gray-600">ช่อดอกไม้</div>
                </div>
                <div className="text-center bg-gray-50 p-4 rounded-lg">
                  <div className="text-2xl mb-2">🎁</div>
                  <div className="text-xl font-bold text-purple-600">{receivedStats.gift_box || 0}</div>
                  <div className="text-sm text-gray-600">ของขวัญ</div>
                </div>
                <div className="text-center bg-gray-50 p-4 rounded-lg">
                  <div className="text-2xl mb-2">💰</div>
                  <div className="text-xl font-bold text-yellow-600">{receivedStats.total.toLocaleString()}</div>
                  <div className="text-sm text-gray-600">T Coins รวม</div>
                </div>
              </div>
            </div>

            {/* Gift List */}
            <GiftDisplay userId={currentUser.uid} teamId={currentTeam?.id} context={filterContext === 'all' ? null : filterContext} />
          </div>
        )}

        {activeTab === 'sent' && (
          <div className="space-y-6">
            {/* Stats */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">📊 สถิติของขวัญที่ให้</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center bg-gray-50 p-4 rounded-lg">
                  <div className="text-2xl mb-2">🌹</div>
                  <div className="text-xl font-bold text-red-600">{sentStats.rose || 0}</div>
                  <div className="text-sm text-gray-600">ดอกกุหลาบ</div>
                </div>
                <div className="text-center bg-gray-50 p-4 rounded-lg">
                  <div className="text-2xl mb-2">💐</div>
                  <div className="text-xl font-bold text-pink-600">{sentStats.bouquet || 0}</div>
                  <div className="text-sm text-gray-600">ช่อดอกไม้</div>
                </div>
                <div className="text-center bg-gray-50 p-4 rounded-lg">
                  <div className="text-2xl mb-2">🎁</div>
                  <div className="text-xl font-bold text-purple-600">{sentStats.gift_box || 0}</div>
                  <div className="text-sm text-gray-600">ของขวัญ</div>
                </div>
                <div className="text-center bg-gray-50 p-4 rounded-lg">
                  <div className="text-2xl mb-2">💰</div>
                  <div className="text-xl font-bold text-yellow-600">{sentStats.total.toLocaleString()}</div>
                  <div className="text-sm text-gray-600">T Coins รวม</div>
                </div>
              </div>
            </div>

            {/* Sent Gifts List */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">📤 ของขวัญที่ให้</h3>
              {filteredSentGifts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">🎁</div>
                  <p>ยังไม่มีการให้ของขวัญ</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {filteredSentGifts.map((gift) => (
                    <div
                      key={gift.id || gift.giftId}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{gift.giftIcon}</span>
                        <div>
                          <div className="font-medium">{gift.giftName}</div>
                          <div className="text-sm text-gray-600">
                            ให้ {gift.toUserName}
                          </div>
                          {gift.context && (
                            <div className="text-xs text-gray-500 mt-1">
                              {getContextLabel(gift.context)}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-blue-600">
                          {gift.price.toLocaleString()} T Coins
                        </div>
                        <div className="text-xs text-gray-500">
                          {gift.timestamp?.toDate ? 
                            gift.timestamp.toDate().toLocaleDateString('th-TH') :
                            new Date(gift.timestamp).toLocaleDateString('th-TH')
                          }
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'exchange' && (
          <div>
            <CoinsExchange />
          </div>
        )}
      </div>

      <NurseBottomNavigation />
    </div>
  );
};

export default GiftHistoryPage;

